// Lifecycle timeline for a single trade -- the centerpiece of the v4
// "Timeline" trade-detail layout (Images/Wickscope/WickScope Trade Detail -
// Timeline.html). It narrates the trade as a vertical sequence of events:
// an "Opened" milestone, one entry per execution (coloured buy vs. sell),
// "Held" gaps where the position sat idle, and a closing milestone carrying
// the realised P&L. It reads the same FillResponse data the Executions table
// tabulates, so the two reconcile exactly -- this view is the human-readable
// companion, the table is the audit grid.

import { useState, type ReactNode } from "react";

import type { components } from "../../api/schema";
import { formatTime } from "../../lib/date";
import {
  formatMoney,
  formatPrice,
  formatSignedMoney,
  isZeroDecimal,
  sumDecimals,
} from "../../lib/money";
import {
  FILL_ACTION_EXECUTION_LABELS,
  formatHoldDuration,
  formatPercent,
  formatQuantity,
  formatRMultiple,
} from "../../lib/tradeFormat";

type TradeResponse = components["schemas"]["TradeResponse"];
type FillResponse = components["schemas"]["FillResponse"];

// A held gap is only worth its own row once the position sat idle for at
// least an hour -- below that the connecting line already conveys the pause,
// and a scalp's sub-minute gaps would flood the timeline.
const HELD_GAP_THRESHOLD_SECONDS = 3600;

// Dot variants mirror the mockup's `.tldot` modifiers (o / buy / sell / done /
// held). `close` carries the realised outcome so its colour comes from `tone`.
export type TimelineDot = "open" | "buy" | "sell" | "held" | "close";

export interface TimelineEvent {
  id: string;
  dot: TimelineDot;
  title: string;
  // Small mono chip trailing the title (a fill's fee); null when none.
  fee: string | null;
  // Mono sub-line under the title; null collapses it.
  meta: string | null;
  // Accent-italic emphasis on the meta line (mockup `.tlm.hl`) -- used the
  // moment the running position returns to flat.
  emphasis: boolean;
  // Title colour for the closing milestone: profit (pos) / loss (neg) /
  // neutral (default, also every non-close row).
  tone: "default" | "pos" | "neg";
}

function isBuy(action: FillResponse["action"]): boolean {
  return action === "bto" || action === "btc";
}

// Flatten and order every leg's fills chronologically -- the spine the
// timeline (and the running net position) is built on.
function chronologicalFills(trade: TradeResponse): FillResponse[] {
  return trade.legs
    .flatMap((leg) => leg.fills)
    .slice()
    .sort(
      (a, b) =>
        new Date(a.executed_at).getTime() - new Date(b.executed_at).getTime(),
    );
}

// Turn a trade into its ordered lifecycle events. Pure and serialisable so the
// composition is unit-testable without rendering. Returns an empty array for a
// trade that has not opened and has no fills (caller renders an empty state).
export function buildTimelineEvents(trade: TradeResponse): TimelineEvent[] {
  const currency = trade.currency;
  const fills = chronologicalFills(trade);
  const events: TimelineEvent[] = [];

  if (trade.opened_at !== null) {
    const direction = trade.legs[0]?.direction;
    const entryPrice =
      trade.average_entry_price ?? fills[0]?.price_amount ?? null;
    const metaParts: string[] = [];
    if (direction !== undefined) metaParts.push(titleCase(direction));
    if (entryPrice !== null) {
      metaParts.push(`entry ${formatPrice(entryPrice, { currency })}`);
    }
    events.push({
      id: "open",
      dot: "open",
      title: "Opened position",
      fee: null,
      meta: metaParts.length > 0 ? metaParts.join(" · ") : null,
      emphasis: false,
      tone: "default",
    });
  }

  let running = "0";
  let previousAt: string | null = null;
  for (const fill of fills) {
    const executedMs = new Date(fill.executed_at).getTime();
    if (previousAt !== null) {
      const gapSeconds = (executedMs - new Date(previousAt).getTime()) / 1000;
      if (gapSeconds >= HELD_GAP_THRESHOLD_SECONDS) {
        events.push({
          id: `held-${fill.fill_id}`,
          dot: "held",
          title: `Held ${formatHoldDuration(Math.round(gapSeconds))}`,
          fee: null,
          meta: null,
          emphasis: false,
          tone: "default",
        });
      }
    }

    const buy = isBuy(fill.action);
    // Exact running net position and flat test -- a float `=== 0` never reads
    // flat once fractional-share fills are involved (0.1 + 0.2 !== 0.3).
    running = sumDecimals([running, buy ? fill.quantity : `-${fill.quantity}`]);
    const flat = isZeroDecimal(running);
    const label = FILL_ACTION_EXECUTION_LABELS[fill.action];
    const feeAmount = Number(fill.fees_amount);

    events.push({
      id: fill.fill_id,
      dot: buy ? "buy" : "sell",
      title: `${label} ${formatQuantity(fill.quantity)} @ ${formatPrice(fill.price_amount, { currency: fill.price_currency })}`,
      fee:
        Number.isFinite(feeAmount) && feeAmount > 0
          ? `${formatMoney(fill.fees_amount, { currency: fill.fees_currency })} fee`
          : null,
      meta: `${formatTime(fill.executed_at)} · ${flat ? "position flat" : `position ${formatQuantity(running)}`}`,
      emphasis: flat,
      tone: "default",
    });
    previousAt = fill.executed_at;
  }

  if (trade.closed_at !== null) {
    const pnl = Number(trade.realized_pnl_amount);
    const tone: TimelineEvent["tone"] =
      pnl > 0 ? "pos" : pnl < 0 ? "neg" : "default";
    const metaParts = [
      formatPercent(trade.realized_return_pct),
      formatRMultiple(trade.r_multiple),
    ].filter((part) => part !== "—");
    events.push({
      id: "close",
      dot: "close",
      title: `Closed ${formatSignedMoney(trade.realized_pnl_amount, { currency })}`,
      fee: null,
      meta: metaParts.length > 0 ? metaParts.join(" · ") : null,
      emphasis: false,
      tone,
    });
  }

  return events;
}

function titleCase(value: string): string {
  return value.length === 0 ? value : value[0]!.toUpperCase() + value.slice(1);
}

// Dot colour by variant. `close` defers to the row's tone so a winning close
// reads green, a losing close red (mockup `.tldot.done` is always green; we
// keep the outcome legible instead).
const DOT_CLASS: Record<Exclude<TimelineDot, "close">, string> = {
  open: "bg-on-surface",
  buy: "bg-success",
  sell: "bg-primary",
  held: "bg-outline-variant",
};

function dotClassName(event: TimelineEvent): string {
  if (event.dot === "close") {
    return event.tone === "neg"
      ? "bg-danger"
      : event.tone === "pos"
        ? "bg-success"
        : "bg-on-surface";
  }
  return DOT_CLASS[event.dot];
}

const TONE_TEXT: Record<TimelineEvent["tone"], string> = {
  default: "text-on-surface",
  pos: "text-success",
  neg: "text-danger",
};

// A long trade (a scalp with dozens of fills, or many legs) would otherwise
// grow an unbounded timeline. Past this many events we collapse the middle
// into one expandable node, always keeping the first HEAD and last TAIL rows
// (so the "Opened" and "Closed" milestones stay visible). The user expands on
// demand, so the default render stays compact next to the chart.
const COLLAPSE_THRESHOLD = 9;
const HEAD_COUNT = 4;
const TAIL_COUNT = 3;

export function TradeTimeline({ trade }: { trade: TradeResponse }): ReactNode {
  const events = buildTimelineEvents(trade);
  const [expanded, setExpanded] = useState(false);

  if (events.length === 0) {
    return <p className="text-sm text-on-surface-variant">No timeline yet.</p>;
  }

  const collapsible = events.length > COLLAPSE_THRESHOLD;
  const hiddenCount = events.length - HEAD_COUNT - TAIL_COUNT;
  const showCollapsed = collapsible && !expanded;

  const head = showCollapsed ? events.slice(0, HEAD_COUNT) : events;
  const tail = showCollapsed ? events.slice(events.length - TAIL_COUNT) : [];

  return (
    <ol className="relative flex flex-col pl-1.5">
      {head.map((event, index) => (
        <TimelineRow
          key={event.id}
          event={event}
          isLast={!showCollapsed && tail.length === 0 && index === head.length - 1}
        />
      ))}

      {showCollapsed ? (
        <li className="relative flex gap-3 pb-4">
          <span
            aria-hidden="true"
            className="absolute left-[5px] top-3.5 bottom-[-2px] w-0.5 bg-outline-variant"
          />
          <span
            aria-hidden="true"
            className="relative z-10 mt-1 ml-0.5 h-2 w-2 shrink-0 rounded-full bg-outline-variant ring-[3px] ring-surface-container"
          />
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="text-[13px] font-semibold text-primary hover:text-primary-hover"
          >
            Show {hiddenCount} more {hiddenCount === 1 ? "event" : "events"}
          </button>
        </li>
      ) : null}

      {tail.map((event, index) => (
        <TimelineRow
          key={event.id}
          event={event}
          isLast={index === tail.length - 1}
        />
      ))}

      {collapsible && expanded ? (
        <li className="mt-1 pl-6">
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="text-xs font-semibold text-on-surface-muted hover:text-on-surface"
          >
            Show less
          </button>
        </li>
      ) : null}
    </ol>
  );
}

function TimelineRow({
  event,
  isLast,
}: {
  event: TimelineEvent;
  isLast: boolean;
}): ReactNode {
  const held = event.dot === "held";
  return (
    <li className={`relative flex gap-3 ${isLast ? "pb-0" : "pb-4"}`}>
      {/* Connecting spine: continues down to the next row, hidden on the last. */}
      {isLast ? null : (
        <span
          aria-hidden="true"
          className="absolute left-[5px] top-3.5 bottom-[-2px] w-0.5 bg-outline-variant"
        />
      )}
      <span
        aria-hidden="true"
        className={`relative z-10 mt-0.5 shrink-0 rounded-full ring-[3px] ring-surface-container ${
          held ? "ml-0.5 h-2 w-2" : "h-3 w-3"
        } ${dotClassName(event)}`}
      />
      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-1">
          <span
            className={`text-[13px] font-semibold ${held ? "text-on-surface-muted" : TONE_TEXT[event.tone]}`}
          >
            {event.title}
          </span>
          {event.fee !== null ? (
            <span className="font-data text-[10px] font-semibold text-on-surface-muted">
              {event.fee}
            </span>
          ) : null}
        </div>
        {event.meta !== null ? (
          <div
            className={`mt-0.5 font-data text-[11.5px] ${
              event.emphasis
                ? "italic text-primary-hover"
                : "text-on-surface-muted"
            }`}
          >
            {event.meta}
          </div>
        ) : null}
      </div>
    </li>
  );
}
